import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Smartphone } from "lucide-react";
import { panelFrameStyle, panelInnerStyle } from "../ui/card-frame";
import { FLOAT_FRAME_SHADOW, popOrigin, useMenu, usePresence, type MenuGroup } from "../ui/use-presence";
import { track } from "../analytics/posthog";
import { APP_MUTED, APP_NAVY, AppIcon, AppQr, StoreBadges } from "./app-promo-parts";
import { isInAppWebView } from "./app-promo-rules";
import { BEVEL_CHIP_ON, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";

const SOFT_TINT = "rgba(17,14,57,0.055)";
const RADIUS = 22;

/**
 * "Get the app" in the computer header (lab round 26, item 199). The QR and
 * the store badges used to arrive as a card floating bottom-right, which sat
 * on an activity page's "Watch video" and "All photos" buttons until it was
 * closed. Here they are one click away on every page and never cover one.
 *
 * Hidden inside the app's own web view, where it would point at itself.
 */
export function AppHeaderButton({ group }: { readonly group?: MenuGroup }) {
  const { t, i18n } = useTranslation();
  const menu = useMenu(group, "app");
  const presence = usePresence(menu.open);
  const [inApp, setInApp] = useState(true);

  useEffect(() => {
    setInApp(isInAppWebView(window as unknown as Parameters<typeof isInAppWebView>[0]));
  }, []);

  useEffect(() => {
    if (menu.open) track("app_promo_shown", { surface: "header" });
  }, [menu.open]);

  if (inApp) return null;

  return (
    <div ref={menu.ref} className="relative shrink-0">
      <button
        type="button"
        onClick={menu.toggle}
        aria-haspopup="dialog"
        aria-expanded={menu.open}
        aria-label={t("app_promo.header_button")}
        className="mj-press inline-flex h-10 min-w-10 shrink-0 items-center justify-center gap-1.5 rounded-full text-[13px] font-semibold xl:ps-3 xl:pe-3.5"
        style={menu.open ? { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON } : { background: SOFT_TINT, color: APP_NAVY }}
      >
        <Smartphone size={15} strokeWidth={2.2} aria-hidden />
        {/* Words from 1280px: at 1024 they squeezed the header search down
            to "Cherch" in French. The icon keeps its name for screen readers. */}
        <span className="hidden xl:inline" aria-hidden="true">
          {t("app_promo.header_button")}
        </span>
      </button>

      {presence.mounted ? (
        <div
          ref={presence.ref}
          role="dialog"
          aria-label={t("app_promo.dialog_label")}
          data-state={presence.state}
          data-instant={menu.instant ? "true" : undefined}
          className={`mj-pop absolute end-0 top-[calc(100%+8px)] z-[var(--z-dropdown)] w-[320px] ${
            presence.state === "closed" ? "pointer-events-none" : ""
          }`}
          style={{ ...panelFrameStyle(RADIUS), boxShadow: FLOAT_FRAME_SHADOW, transformOrigin: popOrigin("end", i18n.dir()) }}
        >
          <div className="p-4" style={panelInnerStyle(RADIUS)}>
            <div className="flex items-start gap-3">
              <AppIcon size={42} />
              <div className="min-w-0 flex-1">
                <p className="text-[15px] font-semibold leading-snug" style={{ color: APP_NAVY }}>
                  {t("app_promo.popup_title")}
                </p>
                <p className="mt-0.5 text-balance text-[12.5px] font-medium leading-snug" style={{ color: APP_MUTED }}>
                  {t("app_promo.popup_sub")}
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3 rounded-[16px] p-2.5" style={{ background: "rgba(17,14,57,0.04)" }}>
              <AppQr size={78} />
              <div className="min-w-0">
                <p className="text-[12.5px] font-semibold" style={{ color: APP_NAVY }}>
                  {t("app_promo.scan")}
                </p>
                <div className="mt-1.5">
                  <StoreBadges
                    height={34}
                    onPick={(store) => track("app_promo_store_click", { surface: "header", store })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
